(() => {
  const T = window.theme || {};
  T.routes = T.routes || {};
  T.strings = T.strings || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const escapeHtml = (str) =>
    String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  // Fills {{ name }} placeholders in a translated string from the theme's language files.
  const fill = (str, vars) =>
    String(str || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => (vars[key] !== undefined ? vars[key] : ''));

  /* ---------- Money ---------- */

  function formatMoney(cents, format = T.moneyFormat || '${{amount}}') {
    const value = Number(cents) || 0;
    const fmt = (amount, decimals, thousands = ',', decimal = '.') => {
      const [whole, frac] = (amount / 100).toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
        default: return fmt(value, 2);
      }
    });
  }
  T.formatMoney = formatMoney;

  /* ---------- Toast ---------- */

  function toast(html) {
    let el = $('#ThemeToast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'ThemeToast';
      el.className = 'toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.innerHTML = html;
    el.classList.add('is-visible');
    clearTimeout(el.hideTimer);
    el.hideTimer = setTimeout(() => el.classList.remove('is-visible'), 4500);
  }

  /* ---------- Cart ---------- */

  async function updateCartCount() {
    try {
      const cart = await fetch(`${T.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    } catch (e) {
      /* the count refreshes on the next page load */
    }
  }

  async function addToCart(items) {
    const res = await fetch(`${T.routes.cartAdd}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || T.strings.error);
    await updateCartCount();
    toast(`${escapeHtml(T.strings.added)} · <a href="${T.routes.cart}">${escapeHtml(T.strings.viewCart)}</a>`);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    return data;
  }
  T.addToCart = addToCart;

  // Quick add buttons (search results, product cards)
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    try {
      await addToCart([{ id: Number(btn.dataset.quickAdd), quantity: 1 }]);
    } catch (err) {
      toast(escapeHtml(err.message));
    } finally {
      btn.disabled = false;
    }
  });

  /* ---------- Chat: open Shopify Inbox ---------- */

  // Opens the Shopify Inbox chat if it's switched on (App embeds). Returns false if it isn't on the page.
  function openShopifyChat(message) {
    const chat = document.querySelector('shopify-chat');
    if (chat) {
      try {
        // `open` is a true/false state on this element; show() is what opens the chat panel.
        if (typeof chat.show === 'function') {
          chat.show();
          if (message && typeof chat.sendStarterMessage === 'function') chat.sendStarterMessage(message);
          return true;
        }
        const proto = Object.getPrototypeOf(chat);
        if (proto && typeof proto.open === 'function') {
          proto.open.call(chat);
          return true;
        }
      } catch (e) {
        /* fall through to the button */
      }
      const runtime = chat.shadowRoot && chat.shadowRoot.querySelector('shopify-agent-runtime');
      const button =
        (chat.shadowRoot && chat.shadowRoot.querySelector('button')) ||
        (runtime && runtime.shadowRoot && runtime.shadowRoot.querySelector('button'));
      if (button) {
        button.click();
        return true;
      }
    }
    const legacy = document.querySelector('inbox-online-store-chat');
    const legacyButton = legacy && legacy.shadowRoot && legacy.shadowRoot.querySelector('button');
    if (legacyButton) {
      legacyButton.click();
      return true;
    }
    return false;
  }
  T.openChat = openShopifyChat;

  // Links marked data-open-chat open the chat; without it they go to the contact page.
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-open-chat]');
    if (!link) return;
    if (openShopifyChat(link.dataset.chatMessage)) e.preventDefault();
  });

  // Product cards with a style picker: update price, photo and the Checkout button
  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-card-variant]');
    if (!select) return;
    const card = select.closest('.card');
    const option = select.selectedOptions[0];
    if (!card || !option) return;
    const button = card.querySelector('[data-buy-now]');
    if (button) button.dataset.buyNow = select.value;
    const price = card.querySelector('.price');
    if (price) {
      const p = Number(option.dataset.price);
      const c = Number(option.dataset.compare);
      price.className = `price${c > p ? ' price--sale' : ''}`;
      price.innerHTML = `<span class="price__current">${formatMoney(p)}</span>${c > p ? `<s class="price__compare">${formatMoney(c)}</s>` : ''}`;
    }
    if (option.dataset.image) {
      const img = card.querySelector('.card__img:not(.card__img--hover)');
      if (img) {
        img.removeAttribute('srcset');
        img.src = option.dataset.image;
      }
    }
  });

  // Checkout buttons: add the chosen style and go straight to checkout
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-buy-now]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      await addToCart([{ id: Number(btn.dataset.buyNow), quantity: 1 }]);
      window.location.href = T.routes.checkout || '/checkout';
    } catch (err) {
      toast(escapeHtml(err.message));
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  });

  // Quantity +/- buttons
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    const input = btn.parentElement.querySelector('input[type="number"]');
    if (!input) return;
    const min = input.min === '' ? 0 : Number(input.min);
    const next = Math.max(min, (Number(input.value) || 0) + Number(btn.dataset.qtyStep));
    if (next === Number(input.value)) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // Cart page: update as soon as a quantity changes
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-qty]');
    if (!input || !input.form) return;
    clearTimeout(input.form.updateTimer);
    input.form.updateTimer = setTimeout(() => {
      const update = document.createElement('input');
      update.type = 'hidden';
      update.name = 'update';
      update.value = '1';
      input.form.appendChild(update);
      input.form.submit();
    }, 500);
  });

  // Forms that ask before submitting (for example, deleting an address)
  document.addEventListener('submit', (e) => {
    const msg = e.target.dataset && e.target.dataset.confirm;
    if (msg && !window.confirm(msg)) e.preventDefault();
  });

  /* ---------- Smart search ---------- */

  const synonymMap = (() => {
    const map = {};
    String(T.synonyms || '')
      .split(/\r?\n/)
      .forEach((line) => {
        const [from, to] = line.split('=').map((s) => (s || '').trim().toLowerCase());
        if (from && to) map[from] = to;
      });
    return map;
  })();

  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  function applySynonyms(query) {
    let out = ` ${query.toLowerCase()} `;
    Object.keys(synonymMap).forEach((from) => {
      out = out.replace(new RegExp(`(\\s)${escapeRegExp(from)}(?=\\s)`, 'g'), `$1${synonymMap[from]}`);
    });
    return out.trim();
  }

  // Understands budgets like "sofa under $500" or "table over 300"
  function parseQuery(raw) {
    let q = String(raw || '').trim();
    let max = null;
    let min = null;
    // English and French put the word first ("under 500", "moins de 500 $");
    // Chinese puts it after the number ("500以下", "500 加元以上").
    const under =
      q.match(/(?:\b(?:under|below|less than|max|up to)|moins de|sous|jusqu['’]à|低于|不超过)\s*\$?\s*(\d[\d,\s]*\d|\d)\s*\$?/i) ||
      q.match(/(\d[\d,]*)\s*(?:\$|加元|元)?\s*(?:以下|以内)/);
    if (under) {
      max = parseInt(under[1].replace(/[,\s]/g, ''), 10);
      q = q.replace(under[0], ' ');
    }
    const over =
      q.match(/(?:\b(?:over|above|more than|min|from)|plus de|au-dessus de|à partir de|高于|超过)\s*\$?\s*(\d[\d,\s]*\d|\d)\s*\$?/i) ||
      q.match(/(\d[\d,]*)\s*(?:\$|加元|元)?\s*以上/);
    if (over) {
      min = parseInt(over[1].replace(/[,\s]/g, ''), 10);
      q = q.replace(over[0], ' ');
    }
    q = applySynonyms(q.replace(/\s+/g, ' ').trim());
    return { q, min, max };
  }

  function buildSearchUrl(raw) {
    const { q, min, max } = parseQuery(raw);
    if (!q && min === null && max === null) return null;
    const params = new URLSearchParams();
    let base = T.routes.search;
    if (q) {
      params.set('q', q);
      params.set('type', 'product');
      params.set('options[prefix]', 'last');
    } else {
      base = T.routes.allProducts;
    }
    if (max !== null) params.set('filter.v.price.lte', max);
    if (min !== null) params.set('filter.v.price.gte', min);
    return `${base}?${params.toString()}`;
  }

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.predictive-search');
      this.trending = this.querySelector('template[data-trending]');
      if (!this.form || !this.input || !this.results) return;
      this.cache = new Map();

      let timer;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.onInput(), 180);
      });
      this.input.addEventListener('focus', () => {
        if (!this.input.value.trim()) this.showTrending();
        else if (this.results.innerHTML.trim()) this.open();
      });
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.addEventListener('keydown', (e) => this.onKeydown(e));
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.close();
      });
      this.rotatePlaceholders();
    }

    async onInput() {
      const raw = this.input.value;
      if (!raw.trim()) {
        this.showTrending();
        return;
      }
      const { q, min, max } = parseQuery(raw);
      if (!q) {
        const parts = [];
        if (min !== null) parts.push(fill(T.strings.priceOver, { price: formatMoney(min * 100) }));
        if (max !== null) parts.push(fill(T.strings.priceUnder, { price: formatMoney(max * 100) }));
        this.results.innerHTML = `<div class="ps"><p class="ps__hint">${escapeHtml(T.strings.priceHint)} ${escapeHtml(parts.join(` ${T.strings.priceAnd || 'and'} `))}</p></div>`;
        this.open();
        return;
      }

      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection,query,page',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
        section_id: 'predictive-search',
      });
      const url = `${T.routes.predictiveSearch}?${params.toString()}`;

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.setAttribute('loading', '');

      try {
        let html = this.cache.get(url);
        if (!html) {
          const res = await fetch(url, { signal: this.abortController.signal });
          if (!res.ok) throw new Error(String(res.status));
          html = await res.text();
          this.cache.set(url, html);
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        this.results.innerHTML = section ? section.innerHTML : '';
        if (this.results.innerHTML.trim()) this.open();
        else this.close();
      } catch (err) {
        if (err.name !== 'AbortError') this.close();
      } finally {
        this.removeAttribute('loading');
      }
    }

    onSubmit(e) {
      e.preventDefault();
      const url = buildSearchUrl(this.input.value);
      if (!url) {
        this.input.focus();
        return;
      }
      window.location.href = url;
    }

    search(term) {
      this.input.value = term;
      const url = buildSearchUrl(term);
      if (url) window.location.href = url;
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        this.input.focus();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (this.results.hidden) return;
      const items = $$('a, button', this.results).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      e.preventDefault();
      const i = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        items[(i + 1) % items.length].focus();
      } else if (i <= 0) {
        this.input.focus();
      } else {
        items[i - 1].focus();
      }
    }

    showTrending() {
      if (!this.trending) {
        this.close();
        return;
      }
      this.results.innerHTML = '';
      this.results.appendChild(this.trending.content.cloneNode(true));
      this.open();
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
    }

    close() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
    }

    rotatePlaceholders() {
      const list = (this.dataset.placeholders || '').split(',').map((s) => s.trim()).filter(Boolean);
      if (list.length < 2) return;
      let i = 0;
      setInterval(() => {
        if (document.activeElement === this.input || this.input.value) return;
        i = (i + 1) % list.length;
        this.input.placeholder = list[i];
      }, 3000);
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  // Chips and trending terms: run that search
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-search-term]');
    if (!btn) return;
    e.preventDefault();
    const scope = btn.closest('predictive-search') || (btn.closest('.shopify-section') && $('predictive-search', btn.closest('.shopify-section')));
    if (scope && scope.search) {
      scope.search(btn.dataset.searchTerm);
    } else {
      const url = buildSearchUrl(btn.dataset.searchTerm);
      if (url) window.location.href = url;
    }
  });

  /* ---------- Product page ---------- */

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[data-product-json]');
      if (!json) return;
      try {
        this.data = JSON.parse(json.textContent);
      } catch (e) {
        return;
      }
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-input]')) this.onOptionChange();
      });
      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-thumb]');
        if (thumb) this.showMedia(thumb.dataset.thumb);
      });
    }

    onOptionChange() {
      const fieldsets = $$('[data-option-index]', this);
      const selected = fieldsets.map((fs) => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
      fieldsets.forEach((fs, i) => {
        const label = fs.querySelector('[data-selected-value]');
        if (label) label.textContent = selected[i] || '';
      });
      const variant = this.data.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      this.update(variant);
    }

    update(variant) {
      const button = this.querySelector('[data-add-button]');
      const stock = this.querySelector('[data-stock]');

      if (!variant) {
        if (button) {
          button.disabled = true;
          button.textContent = T.strings.unavailable;
        }
        if (stock) {
          stock.textContent = T.strings.unavailable;
          stock.className = 'product__stock is-muted';
        }
        return;
      }

      $$('input[name="id"]', this).forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      const price = this.querySelector('[data-price]');
      if (price) price.innerHTML = this.priceHtml(variant);

      if (button) {
        button.disabled = !variant.available;
        button.textContent = variant.available ? T.strings.addToCart : T.strings.soldOut;
      }

      if (stock) {
        if (!variant.available) {
          stock.textContent = T.strings.soldOut;
          stock.className = 'product__stock is-muted';
        } else if (variant.low_stock) {
          stock.textContent = T.strings.lowStock;
          stock.className = 'product__stock is-warning';
        } else {
          stock.textContent = T.strings.inStock;
          stock.className = 'product__stock is-success';
        }
      }

      if (variant.featured_media_id) this.showMedia(variant.featured_media_id);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
    }

    priceHtml(v) {
      const onSale = v.compare_at_price && v.compare_at_price > v.price;
      return `<div class="price${onSale ? ' price--sale' : ''}"><span class="price__current">${formatMoney(v.price)}</span>${
        onSale ? `<s class="price__compare">${formatMoney(v.compare_at_price)}</s>` : ''
      }</div>`;
    }

    showMedia(id) {
      const target = $(`[data-media-id="${id}"]`, this);
      if (!target) return;
      $$('[data-media-id]', this).forEach((slide) => {
        slide.hidden = slide !== target;
      });
      $$('[data-thumb]', this).forEach((thumb) => {
        if (thumb.dataset.thumb === String(id)) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }
  }
  customElements.define('product-info', ProductInfo);

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    async onSubmit(e) {
      e.preventDefault();
      const button = this.form.querySelector('[type="submit"][name="add"]');
      const message = this.querySelector('[data-form-message]');
      const data = new FormData(this.form);
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }
      if (message) message.textContent = '';
      try {
        await addToCart([{ id: Number(data.get('id')), quantity: Math.max(1, Number(data.get('quantity')) || 1) }]);
      } catch (err) {
        if (message) {
          message.textContent = err.message;
          message.className = 'form-message is-error';
        }
      } finally {
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Delivery estimate by postal code ---------- */

  // Province names come from the theme's language files (window.theme.strings.provinces).
  // H and J are also Quebec; L, M, N and P are also Ontario.
  const provinceName = (letter) => {
    const key = 'HJ'.includes(letter) ? 'G' : 'LMNP'.includes(letter) ? 'K' : letter;
    return (T.strings.provinces && T.strings.provinces[key]) || '';
  };

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.output = this.querySelector('[data-eta]');
      const button = this.querySelector('[data-estimate-check]');
      if (!this.input || !this.output) return;
      if (button) button.addEventListener('click', () => this.check());
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.check();
        }
      });
      this.input.addEventListener('input', () => this.output.classList.remove('is-error'));
      try {
        const saved = window.localStorage.getItem('hearth:postal');
        if (saved) {
          this.input.value = saved;
          this.check(true);
        }
      } catch (e) {
        /* storage unavailable */
      }
    }

    check(silent) {
      const code = this.input.value.toUpperCase().replace(/\s+/g, '');
      if (!/^[ABCEGHJ-NPRSTVXY]\d[A-Z]\d[A-Z]\d$/.test(code)) {
        if (silent) return;
        this.output.textContent = T.strings.invalidPostal;
        this.output.classList.add('is-error');
        return;
      }
      const letter = code[0];
      const region = 'KLMNP'.includes(letter) ? 'ontario' : 'GHJ'.includes(letter) ? 'quebec' : 'other';
      const days = this.dataset[region];
      this.input.value = `${code.slice(0, 3)} ${code.slice(3)}`;
      this.output.classList.remove('is-error');
      this.output.textContent = '';
      const strong = document.createElement('strong');
      strong.textContent = `${days} ${this.dataset.unit}`;
      this.output.append(`${fill(T.strings.freeTo, { province: provinceName(letter) })} `, strong, T.strings.sentenceEnd || '.');
      try {
        window.localStorage.setItem('hearth:postal', code);
      } catch (e) {
        /* storage unavailable */
      }
    }
  }
  customElements.define('delivery-estimate', DeliveryEstimate);

  /* ---------- Pickup hours: open now? ---------- */

  class StoreHours extends HTMLElement {
    connectedCallback() {
      const open = Number(this.dataset.open);
      const close = Number(this.dataset.close);
      let hour;
      try {
        const parts = new Intl.DateTimeFormat('en-CA', {
          hour: 'numeric',
          hourCycle: 'h23',
          timeZone: this.dataset.tz || 'America/Toronto',
        }).formatToParts(new Date());
        hour = Number(parts.find((p) => p.type === 'hour').value);
      } catch (e) {
        return;
      }
      const label = (h) =>
        fill(h < 12 || h === 24 ? T.strings.timeAm : T.strings.timePm, { h: h % 12 || 12, h24: h % 24 });
      const isOpen = hour >= open && hour < close;
      this.textContent = isOpen
        ? fill(T.strings.openNow, { time: label(close) })
        : fill(T.strings.closedNow, { time: label(open) });
      this.classList.toggle('is-open', isOpen);
    }
  }
  customElements.define('store-hours', StoreHours);

  /* ---------- Complete the room ---------- */

  class CompleteTheRoom extends HTMLElement {
    async connectedCallback() {
      const url = this.dataset.recommendationsUrl;
      if (url && !this.querySelector('[data-addon]')) {
        try {
          const html = await fetch(url).then((r) => r.text());
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const addons = doc.querySelector('[data-addons]');
          const target = this.querySelector('[data-addons]');
          if (addons && target) target.innerHTML = addons.innerHTML;
        } catch (e) {
          /* fall through and hide */
        }
      }

      if (!this.querySelector('[data-addon]')) {
        const section = this.closest('.shopify-section');
        if (section) section.hidden = true;
        else this.hidden = true;
        return;
      }

      this.addEventListener('change', () => this.updateTotal());
      const button = this.querySelector('[data-add-selected]');
      if (button) button.addEventListener('click', () => this.addSelected(button));

      document.addEventListener('variant:change', (e) => {
        const main = this.querySelector('[data-main-item]');
        const v = e.detail.variant;
        if (!main || !v) return;
        main.dataset.variantId = v.id;
        main.dataset.price = v.price;
        main.dataset.available = v.available;
        const title = main.querySelector('[data-variant-title]');
        if (title) title.textContent = v.title === 'Default Title' ? '' : v.title;
        const price = main.querySelector('[data-item-price]');
        if (price) price.textContent = formatMoney(v.price);
        this.updateTotal();
      });

      this.updateTotal();
    }

    selected() {
      const items = [];
      const main = this.querySelector('[data-main-item]');
      if (main && main.dataset.available !== 'false') items.push(main);
      $$('[data-addon]', this).forEach((row) => {
        const box = row.querySelector('input[type="checkbox"]');
        if (box && box.checked) items.push(row);
      });
      return items;
    }

    updateTotal() {
      const items = this.selected();
      const total = items.reduce((sum, el) => sum + Number(el.dataset.price || 0), 0);
      const out = this.querySelector('[data-total]');
      if (out) out.textContent = formatMoney(total);
      const button = this.querySelector('[data-add-selected]');
      if (button) button.disabled = items.length === 0;
    }

    async addSelected(button) {
      const items = this.selected().map((el) => ({ id: Number(el.dataset.variantId), quantity: 1 }));
      if (!items.length) return;
      button.disabled = true;
      try {
        await addToCart(items);
      } catch (err) {
        toast(escapeHtml(err.message));
      } finally {
        button.disabled = false;
      }
    }
  }
  customElements.define('complete-the-room', CompleteTheRoom);

  /* ---------- Rotating photos in the homepage image tiles ---------- */

  function startRotators() {
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    $$('[data-rotate]').forEach((box) => {
      const imgs = $$('.mosaic__img', box);
      if (imgs.length < 2 || reduceMotion) return;
      const interval = Number(box.dataset.rotateInterval) || 4000;
      let index = Math.max(0, imgs.findIndex((img) => img.classList.contains('is-active')));
      let paused = false;
      const tile = box.closest('a') || box;
      tile.addEventListener('mouseenter', () => { paused = true; });
      tile.addEventListener('mouseleave', () => { paused = false; });
      tile.addEventListener('focusin', () => { paused = true; });
      tile.addEventListener('focusout', () => { paused = false; });
      const step = () => {
        if (paused || document.hidden) return;
        const next = (index + 1) % imgs.length;
        // Only fade once the next photo has loaded, so a tile never flashes empty
        if (!imgs[next].complete) {
          imgs[next].loading = 'eager';
          return;
        }
        imgs[index].classList.remove('is-active');
        imgs[next].classList.add('is-active');
        index = next;
      };
      // Stagger the tiles so they don't all change at once
      setTimeout(() => setInterval(step, interval), Number(box.dataset.rotateOffset) || 0);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startRotators);
  else startRotators();

  /* ---------- Filters: apply as soon as something changes ---------- */

  document.addEventListener('change', (e) => {
    const form = e.target.closest('form[data-facets]');
    if (!form) return;
    clearTimeout(form.submitTimer);
    form.submitTimer = setTimeout(() => {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (String(value).trim() !== '') params.append(key, value);
      });
      window.location.href = `${window.location.pathname}?${params.toString()}`;
    }, e.target.type === 'number' ? 700 : 0);
  });

  /* ---------- Contact page: fill in the topic or what they searched for ---------- */

  document.addEventListener('DOMContentLoaded', () => {
    const params = new URLSearchParams(window.location.search);
    const topic = params.get('topic');
    const select = $('[data-contact-topic]');
    if (select && topic) {
      const option = Array.from(select.options).find((o) => o.dataset.key === topic);
      if (option) select.value = option.value;
    }
    const query = params.get('q');
    const message = $('[data-contact-message]');
    if (message && query && !message.value) {
      if (select) {
        const find = Array.from(select.options).find((o) => o.dataset.key === 'find');
        if (find) select.value = find.value;
      }
      message.value = fill(T.strings.lookingFor || "I'm looking for: {{ terms }}", { terms: query });
    }

    // Close the mobile menu, dropdowns and language list with Escape or a click outside
    const popups = 'details.menu-drawer[open], details.header__dropdown[open], details.lang__details[open]';
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      $$(popups).forEach((d) => d.removeAttribute('open'));
    });
    document.addEventListener('click', (e) => {
      $$(popups).forEach((d) => {
        if (!d.contains(e.target)) d.removeAttribute('open');
      });
    });
  });
})();
